import AppliedFlowDescription from '../flows/AppliedFlowDescription.react';
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import * as Tool from '../fw/Tool';
import {Button, Alert} from 'react-bootstrap';

import {scenarioRequiresPayPlan} from '../../common/model-api/ScenarioType';
import {FormattedMessage, defineMessages, injectIntl, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import ApplyScenarioMutation from './ApplyScenarioMutation.react';
import ScenarioBoxAsWizard from '../scenarios/ScenarioBoxAsWizard.react';
import ScenarioIcon from '../scenarios/ScenarioIcon.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import ChannelIcon from "../channel-defs/ChannelIcon.react";
import config from '../../common/config';
import {getExtensionURL, sendToExtension, isExtensionPresent} from '../app/ExtensionCommunicate';
import {LoadingLine} from '../loadingLine/renderLoadingPanel.react';
import EventTracker from '../app/EventTracker';
import mutationError from '../channel-defs/mutationError';
import StepperList from '../elements/StepperList.react';
import DetailBox from '../fw/DetailBox.react';
import meQuery from '../queries/MeQuery';
import FlowConfig from '../flows/FlowConfig.react';
import ChannelChooser from './ScenarioChooser.react';
import ScenarioChannelConnect from './ScenarioChannelConnect.react';
import ScenarioPrerequisite from './ScenarioPrerequisite.react';
import Link from 'react-router/lib/Link';
import {
    matchesGrantedChannelDef,
    findGrantedOrgChannel,
    promoteGrantedChannelFromConnectList,
    channelNeedsActivationAfterGrant,
    shouldStayOnGrantedChannel,
    getGrantedChannelConnectStep,
    resolveGrantedConfigurationStep,
} from './scenarioApplyOAuthReturn';
import {
    APPLY_RESULT,
    applySuccessFetchVariables,
    classifyApplyScenarioResult,
    getFirstUnselectedChooserStep,
    hasAllChooserSelections,
    mapApplyChannelsToSelect,
    pinChannelSelectToChooserGroups,
    resolveAppliedDescriptionFlow,
    resolveApplyResultFlow,
    resolveMissingChooserRewind,
    resolvePostConnectWizardStep,
    shouldRenderEmbeddedFlowConfig,
    shouldRewindIncompleteApply,
    shouldShowCouldNotCreateFlow,
    shouldShowFinishAppliedDescription,
} from './scenarioApplyResult';
import {buildScenarioApplyClientDiag, logScenarioApplyClientDiag, logScenarioApplyClientDiagOnce} from './scenarioApplyDiag';

import './Scenario.scss';
import { User } from '../../common/types/types';
import { getUISettings } from '../app/UISettings.react';

interface ScenarioApplyProps {
 intl: IntlShape;
 location?: {
  query?: {[key: string]: any};
  search?: string;
 };
 relay: object;
 user: User;
}

const bankGuideMessages = defineMessages({
    title: {
        id: 'scenarioApply.bankGuide.title',
        defaultMessage: 'Automatic Download of Bank Statements to ABRA Flexi',
    },
    intro: {
        id: 'scenarioApply.bankGuide.intro',
        defaultMessage: 'This guide connects your bank account to ABRA Flexi. After activation, account transactions are downloaded automatically every hour throughout the day and created in the Bank agenda.',
    },
    setupTitle: {
        id: 'scenarioApply.bankGuide.setupTitle',
        defaultMessage: 'Setup Process',
    },
    step1Label: {
        id: 'scenarioApply.bankGuide.step1Label',
        defaultMessage: 'Bank connection',
    },
    step1Body: {
        id: 'scenarioApply.bankGuide.step1Body',
        defaultMessage: 'depending on the bank, you either enter an access credential issued by the bank (token or certificate), or you are redirected to internet banking where you confirm account access. Login credentials always remain on the bank side.',
    },
    step2Label: {
        id: 'scenarioApply.bankGuide.step2Label',
        defaultMessage: 'Transfer settings',
    },
    step2Body: {
        id: 'scenarioApply.bankGuide.step2Body',
        defaultMessage: 'you choose the bank document type, target bank account in Flexi, the date from which payments should be transferred, and the account currency.',
    },
    step3Label: {
        id: 'scenarioApply.bankGuide.step3Label',
        defaultMessage: 'Automatic matching (optional)',
    },
    step3Body: {
        id: 'scenarioApply.bankGuide.step3Body',
        defaultMessage: 'after each import, payments can be matched immediately with documents: incoming payments with issued invoices and credit notes of received invoices, outgoing payments with received invoices and credit notes of issued invoices. By default, matching is performed when both variable symbol and amount match; another strategy can be selected optionally.',
    },
    step4Label: {
        id: 'scenarioApply.bankGuide.step4Label',
        defaultMessage: 'Activation',
    },
    step4Body: {
        id: 'scenarioApply.bankGuide.step4Body',
        defaultMessage: 'the connection starts once you save the settings.',
    },
    guidesTitle: {
        id: 'scenarioApply.bankGuide.guidesTitle',
        defaultMessage: 'Guides',
    },
    completeGuideLabel: {
        id: 'scenarioApply.bankGuide.completeGuideLabel',
        defaultMessage: 'Complete guide',
    },
    completeGuideLinkText: {
        id: 'scenarioApply.bankGuide.completeGuideLinkText',
        defaultMessage: 'Automatic online bank connection',
    },
    bankDetailLabel: {
        id: 'scenarioApply.bankGuide.bankDetailLabel',
        defaultMessage: 'Bank setup details',
    },
    couldNotCreateFlow: {
        id: 'scenarioApply.couldNotCreateFlow',
        defaultMessage: 'Scenario could not be applied. Please try again later.',
    },
});

export class ScenarioApply extends React.Component<ScenarioApplyProps> {

    typeName = 'scenarios';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        let scenario = this.getScenario();
        let channels = this.props.user.organisation.channels;
        const preconfiguredFlowId = ScenarioApply.getFlowIdFromQueryParams(props);
        const callbackOnSave = ScenarioApply.getCallbackOnSaveFromQueryParams(props);
        const closeWindow = ScenarioApply.getCloseWindowFromQueryParams(props);
        const showScenarioInfoStep = ScenarioApply.getShowScenarioInfoStepFromQueryParams(props);
        const grantedChannelId = ScenarioApply.getGrantedChannelIdFromQueryParams(props);
        const grantedStep = ScenarioApply.getGrantedStepFromQueryParams(props);
        const grantedChannelDefId = ScenarioApply.getRawQueryParams(props).grantedChannelDefId || null;

        const presetSelection = ScenarioApply.getPresetSelectionData(props, scenario, channels);

        if (typeof window !== 'undefined') {
            // Debug URL-driven scenario apply behavior in local environment.
            console.error('[ScenarioApply] init', {
                rawParams: ScenarioApply.getRawQueryParams(props),
                preconfiguredFlowId,
                closeWindow,
                showScenarioInfoStep,
                grantedChannelId,
                grantedStep,
                skippedGroups: Object.keys(presetSelection.skippedGroups || {}),
                lockedGroups: Object.keys(presetSelection.lockedGroups || {}),
                selectedGroups: Object.keys(presetSelection.channelSelect || {}),
            });
        }

        logScenarioApplyClientDiag('client.init', {
            scenarioId: scenario && (scenario.realId || scenario.id),
            organisationId: this.props.user && this.props.user.organisation && (this.props.user.organisation.realId || this.props.user.organisation.id),
            grantedStep,
            grantedChannelId,
            grantedChannelDefId,
            skippedGroups: Object.keys(presetSelection.skippedGroups || {}),
            lockedGroups: Object.keys(presetSelection.lockedGroups || {}),
            selectedGroups: Object.keys(presetSelection.channelSelect || {}),
        });

        let {alreadyConnectedChannels, channelsToConnect} = this.getChannelsToConnect(scenario, channels);
        alreadyConnectedChannels = ScenarioApply.filterBySkippedGroup(alreadyConnectedChannels, presetSelection.skippedGroups);
        channelsToConnect = ScenarioApply.filterBySkippedGroup(channelsToConnect, presetSelection.skippedGroups);
        let channelsToSelect = ScenarioApply.filterBySkippedGroup(ScenarioApply.getChannelsToSelect(scenario, channels), presetSelection.skippedGroups);

        // When returning from OAuth (CON-13009 / PM-230 follow-up):
        // - grantedStep 4 → stay (post-grant options)
        // - grantedStep 5 + not activated → stay on activate (ChannelAddStep5)
        // - grantedStep 5 + activated → continue to the *next* wizard step
        let grantedChannelInitialStep = null;
        const channelSelect = Object.assign({}, presetSelection.channelSelect);
        if (grantedChannelId) {
            const prefixSteps = showScenarioInfoStep ? 1 : 0;
            const grantedResolution = resolveGrantedConfigurationStep({
                prefixSteps,
                grantedStep,
                grantedChannelId,
                grantedChannelDefId,
                alreadyConnectedChannels,
                channelsToConnect,
                channelsToSelect,
                orgChannels: channels,
            });
            alreadyConnectedChannels = grantedResolution.alreadyConnectedChannels;
            channelsToConnect = grantedResolution.channelsToConnect;
            Object.assign(channelSelect, grantedResolution.channelSelect);
            grantedChannelInitialStep = grantedResolution.configurationStep;

            if (typeof window !== 'undefined') {
                console.error('[ScenarioApply] grantedChannel resolution', {
                    grantedChannelId,
                    grantedChannelDefId,
                    prefixSteps,
                    grantedStep,
                    grantedChannelInitialStep,
                    stayOnChannel: grantedResolution.stayOnChannel,
                    connectStep: grantedResolution.connectStep,
                    alreadyConnectedChannelIds: alreadyConnectedChannels.map((i) => i.channel ? i.channel.realId : null),
                    channelsToConnectIds: channelsToConnect.map((i) => i.channelDef ? i.channelDef.realId : null),
                });
            }
        }

        const prefinishedAlreadyConnectedCount = alreadyConnectedChannels
            .filter((item) => {
                if (!item.channelDef.canBeAddedOnce) {
                    return false;
                }

                // Don't skip over a channel that just returned from OAuth — it still needs to complete its step.
                if (grantedChannelId && item.channel && item.channel.realId === grantedChannelId) {
                    return false;
                }

                const preset = presetSelection.channelPresetByGroup[item.groupKey];
                return !preset || preset.mode !== 'edit';
            })
            .length;

        const initialConfigurationStep = grantedChannelInitialStep !== null
            ? grantedChannelInitialStep
            : showScenarioInfoStep
                ? 0
                : (ScenarioPrerequisite.show(channelsToConnect) ? 0 : prefinishedAlreadyConnectedCount);

        this.state = {
            wasSuccess: false,
            flowSettingNeeded: false,
            flow: null,
            selectedChannels: [],
            channelSelect,
            channelPresetByGroup: presetSelection.channelPresetByGroup,
            lockedChannelGroups: presetSelection.lockedGroups,
            skippedChannelGroups: presetSelection.skippedGroups,
            preconfiguredFlowId,
            callbackOnSave,
            closeWindow,
            showScenarioInfoStep,
            grantedChannelId,
            grantedStep,
            grantedChannelDefId,
            isSaving: false,
            errors: null,
            configurationStep: initialConfigurationStep,
            activatingChannelId: null,
            alreadyConnectedChannels: alreadyConnectedChannels,
            channelsToConnect: channelsToConnect,
            resultLinks: [],
            scenario: scenario,
            channelsToSelect: channelsToSelect,
            channelsWithConfidentialMode: ScenarioApply.getChannelsWitConfidentialMode(scenario, channels),
            loading: true,
            applyFailed: false,
        };

        this.finalSaving = false;

        // let some time to detect (not)installed extenstion
        setTimeout(() => {
            this.configurationSteps = this.getConfigurationSteps();

            this.setState({
                loading: false
            });
        }, 50);
    }

    componentDidMount() {
        if (this.state.preconfiguredFlowId) {
            this.props.relay.setVariables({
                flowId: this.state.preconfiguredFlowId,
            });
        }

        // When returning from OAuth, the newly created channel may not be in the
        // Relay cache yet. Force a fresh fetch so the step calculation can find it.
        if (this.state.grantedChannelId) {
            const channels = this.props.user.organisation.channels;
            const isAlreadyInData = channels.edges.some(({ node }) =>
                node.realId === this.state.grantedChannelId || node.id === this.state.grantedChannelId
            );

            if (!isAlreadyInData) {
                this._grantedFetchDone = false;
                this.props.relay.forceFetch({}, () => {
                    if (this._grantedFetchDone) {
                        return;
                    }
                    this._grantedFetchDone = true;

                    const scenario = this.getScenario();
                    const freshChannels = this.props.user.organisation.channels;
                    const presetSelection = ScenarioApply.getPresetSelectionData(this.props, scenario, freshChannels);
                    let { alreadyConnectedChannels, channelsToConnect } = this.getChannelsToConnect(scenario, freshChannels);
                    alreadyConnectedChannels = ScenarioApply.filterBySkippedGroup(alreadyConnectedChannels, presetSelection.skippedGroups);
                    channelsToConnect = ScenarioApply.filterBySkippedGroup(channelsToConnect, presetSelection.skippedGroups);
                    let channelsToSelect = ScenarioApply.filterBySkippedGroup(ScenarioApply.getChannelsToSelect(scenario, freshChannels), presetSelection.skippedGroups);

                    const grantedChannelId = this.state.grantedChannelId;
                    const grantedChannelDefId = this.state.grantedChannelDefId;
                    const grantedStep = this.state.grantedStep;
                    const prefixSteps = this.state.showScenarioInfoStep ? 1 : 0;
                    const channelSelectUpdate = Object.assign({}, this.state.channelSelect);

                    const grantedResolution = resolveGrantedConfigurationStep({
                        prefixSteps,
                        grantedStep,
                        grantedChannelId,
                        grantedChannelDefId,
                        alreadyConnectedChannels,
                        channelsToConnect,
                        channelsToSelect,
                        orgChannels: freshChannels,
                    });
                    alreadyConnectedChannels = grantedResolution.alreadyConnectedChannels;
                    channelsToConnect = grantedResolution.channelsToConnect;
                    Object.assign(channelSelectUpdate, grantedResolution.channelSelect);
                    const newConfigStep = grantedResolution.configurationStep;

                    if (typeof window !== 'undefined') {
                        console.error('[ScenarioApply] grantedChannel forceFetch result', {
                            grantedChannelId,
                            grantedChannelDefId,
                            grantedStep,
                            newConfigStep,
                            stayOnChannel: grantedResolution.stayOnChannel,
                            freshChannelRealIds: freshChannels.edges.map(({ node }) => node.realId),
                        });
                    }

                    if (newConfigStep !== null && newConfigStep !== this.state.configurationStep) {
                        this.setState({
                            configurationStep: newConfigStep,
                            alreadyConnectedChannels,
                            channelsToConnect,
                            channelsToSelect,
                            channelSelect: channelSelectUpdate,
                        });
                    } else if (Object.keys(grantedResolution.channelSelect).length > 0) {
                        this.setState({
                            alreadyConnectedChannels,
                            channelsToConnect,
                            channelsToSelect,
                            channelSelect: channelSelectUpdate,
                        });
                    }
                });
            }
        }

        this.maybeStartApply();
    }

    componentDidUpdate() {
        this.maybeStartApply();
    }

    getStepsBeforeFlowSettings() {
        let idx = this.state.showScenarioInfoStep ? 1 : 0;
        const alreadyConnected = (this.state.alreadyConnectedChannels || []).filter((item) => !this.state.skippedChannelGroups[item.groupKey]);
        idx += alreadyConnected.length;
        idx += (this.state.channelsToConnect || []).length;
        idx += (this.state.channelsToSelect || []).length;
        if ((this.state.channelsWithConfidentialMode || []).length > 0) {
            idx += 1;
        }
        return idx;
    }

    isOnFlowSettingsStep() {
        return ScenarioApply.showFlowSettings(this.state.scenario)
            && this.state.configurationStep === this.getStepsBeforeFlowSettings();
    }

    isOnFinishApplyStep() {
        if (ScenarioApply.showFlowSettings(this.state.scenario)) {
            return false;
        }
        let idx = this.getStepsBeforeFlowSettings();
        if (this.needInstallExtensionStep()) {
            idx += 1;
        }
        return this.state.configurationStep === idx;
    }

    resolveMissingChooserRewindFromState() {
        const alreadyConnected = (this.state.alreadyConnectedChannels || []).filter((item) => !this.state.skippedChannelGroups[item.groupKey]);
        return resolveMissingChooserRewind({
            alreadyRewound: Boolean(this._didRewindMissingChooser),
            currentStep: this.state.configurationStep,
            prefixSteps: this.state.showScenarioInfoStep ? 1 : 0,
            alreadyConnectedCount: alreadyConnected.length,
            channelsToConnectCount: (this.state.channelsToConnect || []).length,
            channelsToSelect: this.state.channelsToSelect,
            channelSelect: this.state.channelSelect,
        });
    }

    maybeStartApply() {
        if (this.finalSaving || this.state.isSaving || this.state.errors || this.state.flow || this.state.applyFailed || this.state.wasSuccess) {
            logScenarioApplyClientDiagOnce(this, 'client.maybeStart', buildScenarioApplyClientDiag(this, {
                reason: 'blocked',
            }));
            return;
        }
        if (this.state.loading) {
            logScenarioApplyClientDiagOnce(this, 'client.maybeStart', buildScenarioApplyClientDiag(this, {
                reason: 'loading',
            }));
            return;
        }
        if (!this.isOnFlowSettingsStep() && !this.isOnFinishApplyStep()) {
            logScenarioApplyClientDiagOnce(this, 'client.maybeStart', buildScenarioApplyClientDiag(this, {
                reason: 'not-apply-step',
            }));
            return;
        }
        if (!hasAllChooserSelections(this.state.channelsToSelect, this.state.channelSelect)) {
            logScenarioApplyClientDiagOnce(this, 'client.maybeStart', buildScenarioApplyClientDiag(this, {
                reason: 'missing-selection',
            }));
            const rewind = this.resolveMissingChooserRewindFromState();
            if (rewind.shouldRewind) {
                this._didRewindMissingChooser = true;
                logScenarioApplyClientDiag('client.rewindMissingChooser', buildScenarioApplyClientDiag(this, {
                    reason: 'rewind-missing-selection',
                    targetStep: rewind.targetStep,
                }));
                this.setState({
                    configurationStep: rewind.targetStep,
                });
            }
            return;
        }
        logScenarioApplyClientDiag('client.maybeStart', buildScenarioApplyClientDiag(this, {
            reason: 'start-apply',
        }));
        this.finalSaving = true;
        this.onApplyScenarioClick(this.props);
    }

    onApplyScenarioClick(props) {
        this.setState({
            isSaving: true
        });

        if (!props.user.organisation) {
            this.finalSaving = false;
            this.setState({
                isSaving: false,
                applyFailed: true,
                errors: 'Organisation is missing.',
            });
            return;
        }

        let scenario = props.user.scenario;

        if (!scenario) {
            scenario = props.user.scenarios.edges[0].node;
        }

        if (!scenarioRequiresPayPlan(scenario, props.user.organisation.payPlan)) {
            this.finalSaving = false;
            this.setState({
                isSaving: false,
                applyFailed: true,
                errors: 'Organisation pay plan is not sufficient for this scenario.',
            });
            return;
        }

        EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
            event: 'scenario-apply',
            type: 'application'
        });

        return new Promise((resolve, reject) => {
            let onFailure = (transaction) => {
                mutationError(transaction);
                // Without this the wizard stays forever on "Activating scenario…"
                // (finalSaving blocks a retry and isSaving never clears).
                this.finalSaving = false;
                logScenarioApplyClientDiag('client.apply-failure', buildScenarioApplyClientDiag(this, {
                    reason: 'mutation-failure',
                }));
                this.setState({
                    isSaving: false,
                    applyFailed: true,
                    errors: 'Scenario activation failed. Please try again.',
                });

                reject();
            };

            let onSuccess = (response) => {
                const applyResult = classifyApplyScenarioResult(response && response.applyScenario);
                logScenarioApplyClientDiag('client.apply-success', buildScenarioApplyClientDiag(this, {
                    resultType: applyResult && applyResult.type,
                    flowId: applyResult && applyResult.flow && (applyResult.flow.realId || applyResult.flow.id),
                    hasErrors: !!(applyResult && applyResult.errors),
                }));

                if (applyResult.type === APPLY_RESULT.ERROR) {
                    this.finalSaving = false;
                    return this.setState({
                        isSaving: false,
                        applyFailed: true,
                        errors: applyResult.errors,
                    });
                }

                if (applyResult.type === APPLY_RESULT.INCOMPLETE) {
                    const currentScenario = this.getScenario() || scenario;
                    const orgChannels = props.user.organisation.channels;
                    const recomputedSelect = ScenarioApply.filterBySkippedGroup(
                        ScenarioApply.getChannelsToSelect(currentScenario, orgChannels),
                        this.state.skippedChannelGroups
                    );
                    const fromPayload = ScenarioApply.filterBySkippedGroup(
                        mapApplyChannelsToSelect(applyResult.channelsToSelect, ScenarioApply.getChannelGroupKey),
                        this.state.skippedChannelGroups
                    );
                    const nextSelect = recomputedSelect.length > 0
                        ? recomputedSelect
                        : (fromPayload.length > 0 ? fromPayload : this.state.channelsToSelect);
                    const nextConfidential = applyResult.channelsWithConfidentialMode.length > 0
                        ? applyResult.channelsWithConfidentialMode
                        : this.state.channelsWithConfidentialMode;
                    if ((nextSelect || []).length === 0 && (nextConfidential || []).length === 0) {
                        this.finalSaving = false;
                        return this.setState({
                            isSaving: false,
                            applyFailed: true,
                            errors: props.intl.formatMessage(bankGuideMessages.couldNotCreateFlow),
                        });
                    }
                    const pinnedSelect = pinChannelSelectToChooserGroups(this.state.channelSelect, nextSelect);
                    if (!shouldRewindIncompleteApply(nextSelect, pinnedSelect)) {
                        if (this._incompletePinnedRetry) {
                            this.finalSaving = false;
                            return this.setState({
                                isSaving: false,
                                applyFailed: true,
                                channelSelect: pinnedSelect,
                                channelsToSelect: nextSelect,
                                channelsWithConfidentialMode: nextConfidential,
                            });
                        }
                        this._incompletePinnedRetry = true;
                        this.finalSaving = false;
                        this.setState({
                            isSaving: false,
                            wasSuccess: false,
                            applyFailed: false,
                            flow: null,
                            channelSelect: pinnedSelect,
                            channelsToSelect: nextSelect,
                            channelsWithConfidentialMode: nextConfidential,
                        });
                        resolve();
                        return;
                    }
                    const alreadyConnected = (this.state.alreadyConnectedChannels || []).filter((item) => !this.state.skippedChannelGroups[item.groupKey]);
                    const prefixSteps = this.state.showScenarioInfoStep ? 1 : 0;
                    const targetStep = getFirstUnselectedChooserStep({
                        prefixSteps,
                        alreadyConnectedCount: alreadyConnected.length,
                        channelsToConnectCount: (this.state.channelsToConnect || []).length,
                        channelsToSelect: nextSelect,
                        channelSelect: pinnedSelect,
                    });
                    const chooserStep = targetStep != null
                        ? targetStep
                        : (prefixSteps + alreadyConnected.length + (this.state.channelsToConnect || []).length);
                    this._didRewindMissingChooser = false;
                    this.finalSaving = false;
                    this.setState({
                        isSaving: false,
                        wasSuccess: false,
                        applyFailed: false,
                        flow: null,
                        channelSelect: pinnedSelect,
                        channelsToSelect: nextSelect,
                        channelsWithConfidentialMode: nextConfidential,
                        configurationStep: chooserStep,
                    });
                    resolve();
                    return;
                }

                if (applyResult.type === APPLY_RESULT.FAILED) {
                    this.finalSaving = false;
                    return this.setState({
                        isSaving: false,
                        applyFailed: true,
                        errors: props.intl.formatMessage(bankGuideMessages.couldNotCreateFlow),
                    });
                }

                sendToExtension({
                    message: 'dativery-reload'
                });

                const fetchVariables = applySuccessFetchVariables(applyResult.flow, this.state.preconfiguredFlowId);
                if (fetchVariables) {
                    this.props.relay.setVariables(fetchVariables);
                }

                this.setState({
                    isSaving: false,
                    wasSuccess: true,
                    applyFailed: false,
                    resultLinks: applyResult.resultLinks || [],
                    flow: applyResult.flow || null
                });

                resolve();

                props.relay.forceFetch(fetchVariables);
            };

            Relay.Store.commitUpdate(new ApplyScenarioMutation({
                    scenario: scenario,
                    callbackOnSave: this.state.callbackOnSave,
                    organisation: props.user.organisation,
                    args: this.state.preconfiguredFlowId ? {flowId: this.state.preconfiguredFlowId} : null,
                    selectedChannels: Object.keys(this.state.channelSelect).map((key) => {
                        return {
                            channelDef_id: key,
                            channel_id: this.state.channelSelect[key].realId
                        };
                    })}),
                {
                    onFailure,
                    onSuccess
                }
            );
        });

    }

    onChannelSelect(channelDef, channel) {
        const groupKey = ScenarioApply.getChannelGroupKey(channelDef);
        if (this.state.lockedChannelGroups[groupKey] && this.state.channelSelect[groupKey] && this.state.channelSelect[groupKey].realId !== channel.realId) {
            return;
        }

        this._didRewindMissingChooser = false;
        this._incompletePinnedRetry = false;
        let channelSelect = Object.assign({}, this.state.channelSelect);
        channelSelect[groupKey] = channel;
        channelSelect = pinChannelSelectToChooserGroups(channelSelect, this.state.channelsToSelect);
        this.setState({channelSelect: channelSelect});
    }

    static filterBySkippedGroup(list, skippedGroups) {
        return list.filter((item) => !skippedGroups[item.groupKey]);
    }

    static normalizeChannelPresetKey(value) {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-');
    }

    static parsePresetValue(rawValue) {
        if (!rawValue) {
            return null;
        }

        const tokens = String(rawValue)
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);

        if (tokens.length === 0) {
            return null;
        }

        let channelId = null;
        let mode = 'select';
        let locked = false;

        tokens.forEach((token, index) => {
            const lower = token.toLowerCase();
            if (lower === 'skip' || lower === 'edit' || lower === 'select') {
                mode = lower;
                return;
            }

            if (lower === 'locked' || lower === 'lock' || lower === 'readonly') {
                locked = true;
                return;
            }

            if (lower.startsWith('id:')) {
                channelId = token.substring(3).trim();
                return;
            }

            if (!channelId && index === 0) {
                channelId = token;
            }
        });

        if (!channelId) {
            return null;
        }

        return {
            channelId,
            mode,
            locked,
        };
    }

    static getRawQueryParams(props) {
        const raw = {};

        if (props && props.location && props.location.query) {
            Object.keys(props.location.query).forEach((key) => {
                raw[key] = props.location.query[key];
            });
        }

        const search = (props && props.location && props.location.search) || (typeof window !== 'undefined' && window.location ? window.location.search : '');
        if (!search) {
            return raw;
        }

        search
            .replace(/^\?/, '')
            .split('&')
            .filter((item) => item)
            .forEach((part) => {
                const delimiter = part.indexOf('=');
                const encodedKey = delimiter >= 0 ? part.substring(0, delimiter) : part;
                const encodedValue = delimiter >= 0 ? part.substring(delimiter + 1) : '';
                const key = decodeURIComponent(encodedKey || '').trim();
                const value = decodeURIComponent((encodedValue || '').replace(/\+/g, ' '));

                if (key) {
                    raw[key] = value;
                }
            });

        return raw;
    }

    static getChannelPresetsFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        const presets = {};

        Object.keys(rawParams).forEach((paramName) => {
            if (!paramName.startsWith('channel.') && !paramName.startsWith('channels.')) {
                return;
            }

            const key = paramName.replace(/^channels?\./, '').trim();
            const preset = ScenarioApply.parsePresetValue(rawParams[paramName]);
            if (!key || !preset) {
                return;
            }

            presets[ScenarioApply.normalizeChannelPresetKey(key)] = preset;
        });

        return presets;
    }

    static getFlowIdFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.flowId || rawParams.flow_id || rawParams.flow || null;
    }

    static getCallbackOnSaveFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.callbackOnSave || null;
    }

    static getCloseWindowFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.closeWindow || null;
    }

    static getShowScenarioInfoStepFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        const value = rawParams.showScenarioInfoStep || rawParams.showScenarioDescriptionStep || rawParams.showDescriptionStep;

        if (value === undefined || value === null) {
            return false;
        }

        const normalized = String(value).trim().toLowerCase();
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
    }

    static getGrantedChannelIdFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        return rawParams.grantedChannelId || null;
    }

    static getGrantedStepFromQueryParams(props) {
        const rawParams = ScenarioApply.getRawQueryParams(props);
        const val = rawParams.grantedStep ? parseInt(rawParams.grantedStep, 10) : null;
        return val && !isNaN(val) ? val : null;
    }

    static matchesGrantedChannelDef = matchesGrantedChannelDef;
    static findGrantedOrgChannel = findGrantedOrgChannel;
    static promoteGrantedChannelFromConnectList = promoteGrantedChannelFromConnectList;
    static channelNeedsActivationAfterGrant = channelNeedsActivationAfterGrant;
    static shouldStayOnGrantedChannel = shouldStayOnGrantedChannel;
    static getGrantedChannelConnectStep = getGrantedChannelConnectStep;

    static getScenarioGroupsWithAliases(scenario) {
        const scenarioGroups = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (scenarioGroups[groupKey]) {
                return;
            }

            const aliases = {};
            aliases[ScenarioApply.normalizeChannelPresetKey(groupKey)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.channelDef.realId)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.channelDef.id)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.channelDef.name)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.key)] = true;
            aliases[ScenarioApply.normalizeChannelPresetKey(node.ruleType)] = true;

            scenarioGroups[groupKey] = {
                groupKey,
                aliases,
            };
        });

        return scenarioGroups;
    }

    static getPresetSelectionData(props, scenario, channels) {
        const presets = ScenarioApply.getChannelPresetsFromQueryParams(props);
        const scenarioGroups = ScenarioApply.getScenarioGroupsWithAliases(scenario);
        const channelsByGroup = {};

        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelsByGroup[groupKey]) {
                channelsByGroup[groupKey] = [];
            }
            channelsByGroup[groupKey].push(node);
        });

        const channelSelect = {};
        const channelPresetByGroup = {};
        const lockedGroups = {};
        const skippedGroups = {};

        const matchesPresetChannelId = (channel, presetChannelId) => {
            if (!channel || !presetChannelId) {
                return false;
            }

            return channel.realId === presetChannelId || channel.id === presetChannelId;
        };

        const findGroupByChannelId = (presetChannelId) => {
            const candidateGroups = Object.keys(channelsByGroup).filter((groupKey) => {
                if (!scenarioGroups[groupKey]) {
                    return false;
                }

                return (channelsByGroup[groupKey] || []).some((channel) => matchesPresetChannelId(channel, presetChannelId));
            });

            if (candidateGroups.length === 1) {
                return candidateGroups[0];
            }

            return null;
        };

        Object.keys(presets).forEach((presetKey) => {
            const preset = presets[presetKey];
            let matchingGroup = Object.keys(scenarioGroups).find((groupKey) => scenarioGroups[groupKey].aliases[presetKey]);

            if (!matchingGroup) {
                matchingGroup = findGroupByChannelId(preset.channelId);
            }

            if (!matchingGroup) {
                if (typeof window !== 'undefined') {
                    console.error('[ScenarioApply] preset key did not match any scenario group', {presetKey, preset, scenarioGroups: Object.keys(scenarioGroups)});
                }
                return;
            }

            const candidateChannels = channelsByGroup[matchingGroup] || [];
            const selectedChannel = candidateChannels.find((channel) => matchesPresetChannelId(channel, preset.channelId));

            if (!selectedChannel) {
                if (typeof window !== 'undefined') {
                    console.error('[ScenarioApply] preset channel was not found in organisation channels', {
                        presetKey,
                        preset,
                        matchingGroup,
                        candidateChannelIds: candidateChannels.map((ch) => ch.realId || ch.id),
                    });
                }
                return;
            }

            channelSelect[matchingGroup] = selectedChannel;
            channelPresetByGroup[matchingGroup] = preset;

            if (preset.locked) {
                lockedGroups[matchingGroup] = true;
            }

            if (preset.mode === 'skip') {
                skippedGroups[matchingGroup] = true;
            }
        });

        return {
            channelSelect,
            channelPresetByGroup,
            lockedGroups,
            skippedGroups,
        };
    }

    nextStep() {
        this.setState({
            configurationStep: this.state.configurationStep + 1
        });
    }

    getTitle(scenario) {
        const metadata = types[this.typeName];

        let title = Tool.getDataForColumn('title', metadata.fields(), scenario);
        if (!title) {
            title = Tool.getDataForColumn('name', metadata.fields(), scenario);
        }

        return title;
    }

    getScenario() {
        let scenario = this.props.user.scenario;

        if (!scenario) {
            scenario = this.props.user.scenarios.edges[0].node;
        }

        return scenario;
    }

    renderResultLinks() {
        const organisationFlow = this.props.user.organisation && this.props.user.organisation.flow;
        const flow = resolveApplyResultFlow(organisationFlow, this.state.flow);
        const descriptionFlow = resolveAppliedDescriptionFlow(organisationFlow, this.state.flow);
        const scenario = this.state.scenario;
        const showFinishDescription = shouldShowFinishAppliedDescription(
            descriptionFlow,
            Boolean(ScenarioApply.getBankScenarioHelpLink(scenario && scenario.urlFragment))
        );
        return (
            <div>
                {this.state.resultLinks.length ? <h4><FormattedMessage defaultMessage="Do you want to see the scenario in action?" id="channelWizard.links.title" /></h4> : null}
                <div className={"scenario-apply-links" + (flow ? ' withFlow' : '')}>
                {this.state.resultLinks.map((resultLink, idx) => {
                    if (!resultLink.url) {
                        return (
                            <div className="scenario-apply-one" key={idx}>
                                <div className="title">
                                    <FormattedMessage defaultMessage="Open {channelDefName} and go to {sourceDefName}" id="channelWizard.links.name" values={{channelDefName: resultLink.channelDef.name, sourceDefName: resultLink.sourceDef.name}} />
                                </div>
                            </div>
                        );
                    } else if (resultLink.linksToSourceDef) {
                        return (
                            <div className="scenario-apply-one" key={idx}>
                                <div className="title">
                                    <a className="btn btn-default" href={resultLink.url} target="_blank">
                                        <ChannelIcon channelDef={resultLink.channelDef} size={32}/>{" "}
                                        <FormattedMessage defaultMessage="Open {channelDefName} and go to {sourceDefName}" id="channelWizard.links.name" values={{channelDefName: resultLink.channelDef.name, sourceDefName: resultLink.sourceDef.name}} />
                                    </a>
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div className="scenario-apply-one" key={idx}>
                                <div className="title">
                                    <FormattedMessage defaultMessage="Open {channelDefName} and go to {sourceDefName}" id="channelWizard.links.name" values={{channelDefName: resultLink.channelDef.name, sourceDefName: resultLink.sourceDef.name}} />
                                </div>
                                <div className="action">
                                    <a className="btn btn-default" href={resultLink.url} target="_blank">
                                        <ChannelIcon channelDef={resultLink.channelDef} size={32}/>{" "}
                                        <FormattedMessage defaultMessage="Go to {channelDefName}" id="channelWizard.links.actionGo" values={{channelDefName: resultLink.channelDef.name}} /></a>
                                </div>
                            </div>
                        );
                    }
                })}

                {flow ?
                    <div className="scenario-flow-results">
                        <h4><FormattedMessage defaultMessage="Scenario is activated." id="channelWizard.scenaioActivated" /></h4>
                        {showFinishDescription ? <div className="secondStepper">
                            <h4><FormattedMessage defaultMessage="How to use this flow scenario:" id="channelWizard.flowUsage.title" /></h4>
                            <AppliedFlowDescription flow={descriptionFlow} user={this.props.user} />
                        </div> : null}


                        {this.state.closeWindow ?
                            <p className="">
                                <Link className="btn btn-primary" style={{marginRight: '2em'}} onClick={() => window.close()}><FormattedMessage defaultMessage="Close window and go back" id="channelWizard.flowUsage.closeWindow" /></Link>
                                <Link className="btn btn-primary" to={urlMapping.getUrl(types['flows'], "config", flow)}><FormattedMessage defaultMessage="Change settings" id="channelWizard.flowUsage.toFlowConfig" /></Link>
                                
                            </p>
                        :
                            <p><Link className="btn btn-primary" to={urlMapping.getUrl(types['flows'], "view", flow)}><FormattedMessage defaultMessage="Go to flow detail" id="channelWizard.flowUsage.toDetail" /></Link></p>
                        }
                    </div>
                : null}
                </div>
            </div>
        );
    }

    renderChannelChoosers() {
        let channelGroups = (this.state.channelsToSelect || []).map((group) => {
            let {channelSelect, channelsWithConfidentialMode} = this.state;
            const groupKey = group.groupKey;
            const selectedChannel = channelSelect[groupKey];

            return {
                channelDef: group.channelDef,
                groupKey: groupKey,
                channels: group.channels.map((ch) => {
                    const isActive = Boolean(selectedChannel && selectedChannel.realId === ch.realId);
                    const hasError = channelsWithConfidentialMode ? channelsWithConfidentialMode.find(channelWithError => ch.id === channelWithError.id) : false;
                    return {
                        ...ch,
                        isActive,
                        hasError,
                    };
                }),
            };
        });

        // Sort by order field (scenario definition order)
        channelGroups.sort((groupA, groupB) => {
            let groupAOrder = this.state.scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === groupA.channelDef.realId).order;
            let groupBOrder = this.state.scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === groupB.channelDef.realId).order;

            return groupAOrder - groupBOrder;
        });

        return channelGroups.map((channelGroup, index) => {
            const preset = this.state.channelPresetByGroup[channelGroup.groupKey];
            const isPresetEdit = Boolean(preset && preset.mode === 'edit');
            const isLockedPreset = Boolean(this.state.lockedChannelGroups[channelGroup.groupKey] && this.state.channelSelect[channelGroup.groupKey]);

            // Check if the granted channel (returned from OAuth) is in this chooser group.
            const grantedChannelId = this.state.grantedChannelId;
            const grantedStep = this.state.grantedStep;
            const grantedChannelDefId = this.state.grantedChannelDefId;

            const isGrantedInGroup = Boolean(
                grantedChannelId &&
                channelGroup.channels.some((ch) =>
                    ch.realId === grantedChannelId || ch.id === grantedChannelId
                )
            );

            // Also match by channelDef when the specific channel isn't in Relay data yet
            // (e.g. freshly created via OAuth and not yet in the Relay cache).
            const isGrantedGroupByDef = Boolean(
                !isGrantedInGroup &&
                grantedChannelId &&
                grantedChannelDefId &&
                channelGroup.channelDef && (
                    channelGroup.channelDef.realId === grantedChannelDefId ||
                    channelGroup.channelDef.id === grantedChannelDefId ||
                    channelGroup.groupKey === grantedChannelDefId
                )
            );

            const isGrantedGroup = isGrantedInGroup || isGrantedGroupByDef;
            const grantedChannelInGroup = channelGroup.channels.find((ch) =>
                ch.realId === grantedChannelId || ch.id === grantedChannelId
            ) || (isGrantedGroupByDef
                ? { realId: grantedChannelId, id: grantedChannelId, channelDef: channelGroup.channelDef, name: null, activated: false }
                : null);
            const grantedConnectStep = getGrantedChannelConnectStep(grantedStep, grantedChannelInGroup);
            const isGrantedGroupPending = Boolean(
                isGrantedGroup && shouldStayOnGrantedChannel(grantedStep, grantedChannelInGroup)
            );

            // When the granted channel isn't in Relay data, inject a synthetic placeholder
            // so ChannelChooser can navigate to ScenarioChannelConnect for it directly.
            // ScenarioChannelConnect will set channelId in its queryConfig and fetch it individually.
            const channelGroupToPass = (isGrantedGroupByDef && grantedChannelId)
                ? {
                    ...channelGroup,
                    channels: [
                        ...channelGroup.channels,
                        grantedChannelInGroup,
                    ],
                }
                : channelGroup;

            return {
                title: (isPresetEdit && isLockedPreset) || isGrantedGroupPending
                    ? (<FormattedMessage defaultMessage="Configure {app}" id="channelWizard.configureApplicationTitle" values={{app: channelGroup.channelDef.name}} />)
                    : (<FormattedMessage defaultMessage="Select {app} from multiple connections" id="channelWizard.selectApplicationTitle" values={{app: channelGroup.channelDef.name}} />),
                description: (
                    <ChannelChooser
                        key={isGrantedGroupPending ? ('granted-' + grantedConnectStep) : undefined}
                        channelGroup={channelGroupToPass}
                        connectCallback={(channel) => {
                            this.connectChannelStep4Callback(channel);
                        }}
                        nextStep={() => { return this.nextStep(); }}
                        onChannelSelect={(channelDef, channel) => { return this.onChannelSelect(channelDef, channel); }}
                        locked={isGrantedGroupPending ? true : Boolean(this.state.lockedChannelGroups[channelGroup.groupKey])}
                        preselectedChannelId={isGrantedGroupPending ? grantedChannelId : (this.state.channelSelect[channelGroup.groupKey] ? this.state.channelSelect[channelGroup.groupKey].realId : null)}
                        forceConfigure={isPresetEdit || isGrantedGroupPending}
                        initialStep={isGrantedGroupPending ? grantedConnectStep : undefined}
                        grantedChannelId={isGrantedGroupPending ? grantedChannelId : undefined}
                        grantedStep={isGrantedGroupPending ? grantedConnectStep : undefined}
                        organisationId={this.props.user.organisation.realId}
                        scenario={this.state.scenario}
                    />
                )
            };
        });
    }

    /**
     * Group key for "extended" channelDefs.
     * If channelDef is derived (extendedFrom), treat it as the parent for selection/matching.
     */
    static getChannelGroupKey(channelDef) {
        // Prefer raw id field when present (more reliable than nested relation in older queries)
        if (channelDef && channelDef.extendedFrom_id) {
            return channelDef.extendedFrom_id;
        }
        if (channelDef && channelDef.extendedFrom && channelDef.extendedFrom.realId) {
            return channelDef.extendedFrom.realId;
        }
        return channelDef ? channelDef.realId : null;
    }

    renderConfidentialChannels() {
        let confidentialChannelsCount = this.state.channelsWithConfidentialMode.length;

        return (
            <div>
                <p>
                    <FormattedMessage defaultMessage="Scenario requires data caching but confidential mode preventing data caching is activated on {count, plural, one {this channnel} few {these channnels} other {these channnels}}:" id="scenarioApply.chaching.channelsHeader" values={{count: confidentialChannelsCount}} />
                </p>
                <div className="channelsWithConfidentialMode">
                    {
                        this.state.channelsWithConfidentialMode.map((channel, idx) => {
                            return (<div key={idx}>
                                <ChannelIcon channelDef={channel.channelDef} size={32}/>&nbsp;{channel.name ? channel.name : channel.channelDef.name}
                            </div>);
                        })
                    }
                </div>
                <div>
                    <Button className="btn btn-primary" onClick={() => {this.nextStep();}}>
                        <FormattedMessage defaultMessage="Disable confidential mode" id="channel.disableConfidential" />
                    </Button>
                </div>
            </div>
        );
    }

    renderFlowSettings(activeStep) {
        if (!activeStep) {
            return;
        }

        if (this.state.isSaving || (this.finalSaving && !this.state.flow && !this.state.errors)) {
            return (
                <LoadingLine withMessage>
                    <FormattedMessage defaultMessage="Activating scenario…" id="scanarioActivation.message" />
                </LoadingLine>
            );
        }

        if (this.state.errors) {
            return this.renderError();
        }

        if (this.state.flow) {
            try {
                let configOptions = JSON.parse(this.state.flow.configOptions);
                if (Object.keys(configOptions).length === 0) {
                    return this.nextStep();
                }
            } catch (e) {
                /* ignore error */
            }

            const queryConfig = {
                queries: meQuery,
                name: 'User',
                params: {
                    organisationId: this.props.user.organisation.realId,
                    scenarioId: this.state.scenario.realId,
                    flowId: this.state.flow.realId
                }
            };

            return (<Relay.Renderer
                Container={FlowConfig}
                environment={Relay.Store}
                queryConfig={queryConfig}
                render={(state) => {
                    if (!shouldRenderEmbeddedFlowConfig(state)) {
                        return (<LoadingLine withMessage>
                            <FormattedMessage defaultMessage="Loading flow settings…" id="scanarioActivation.loadingFlowSettings" />
                        </LoadingLine>);
                    }
                    return (<FlowConfig {...state.props} createCallback={() => {this.nextStep();}} createStep wrapper />);
                }}
            />);
        }

        if (shouldShowCouldNotCreateFlow({
            applyFailed: this.state.applyFailed,
            errors: this.state.errors,
            flow: this.state.flow,
        })) {
            logScenarioApplyClientDiagOnce(this, 'client.dead-end', buildScenarioApplyClientDiag(this, {
                reason: 'couldNotCreateFlow',
            }));
            return (
                <FormattedMessage
                    defaultMessage="Scenario could not be applied. Please try again later."
                    id="scenarioApply.couldNotCreateFlow"
                />
            );
        }

        if (!hasAllChooserSelections(this.state.channelsToSelect, this.state.channelSelect)) {
            return null;
        }

        return (
            <LoadingLine withMessage>
                <FormattedMessage defaultMessage="Activating scenario…" id="scanarioActivation.message" />
            </LoadingLine>
        );
    }

    getChannelsToConnect(scenario, channels) {
        // Scenario required groups (derived channelDefs are grouped by parent)
        let scenarioGroups = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!scenarioGroups[groupKey]) {
                scenarioGroups[groupKey] = node.channelDef;
            }
        });
        const scenarioGroupKeys = Object.keys(scenarioGroups);

        // Organisation channels grouped by groupKey (parent for derived)
        let channelGrouped = {};
        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelGrouped[groupKey]) {
                channelGrouped[groupKey] = [];
            }
            channelGrouped[groupKey].push(node);
        });

        let channelsToConnect = [];
        let alreadyConnectedChannels = [];

        scenarioGroupKeys.forEach((groupKey) => {
            if (!channelGrouped[groupKey]) {
                channelsToConnect.push({
                    groupKey,
                    channelDef: scenarioGroups[groupKey],
                });
            } else if (channelGrouped[groupKey].length === 1) {
                alreadyConnectedChannels.push({
                    groupKey,
                    channelDef: scenarioGroups[groupKey],
                    channel: channelGrouped[groupKey][0],
                });
            }
        });

        return {
            alreadyConnectedChannels: alreadyConnectedChannels.sort((a, b) => {
                if (a.channelDef.canBeAddedOnce && b.channelDef.canBeAddedOnce) {
                    let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
                    let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
                    return aOrder - bOrder;
                }

                if (a.channelDef.canBeAddedOnce) {
                    return -1;
                }
                if (b.channelDef.canBeAddedOnce) {
                    return 1;
                }

                let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
                let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
                return aOrder - bOrder;
            }),
            channelsToConnect: channelsToConnect.sort((a, b) => {
                let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
                let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
                return aOrder - bOrder;
            }),
        };
    }

    static getChannelsToSelect(scenario, channels) {
        // Scenario groups (derived channelDefs are grouped by parent)
        let scenarioGroups = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!scenarioGroups[groupKey]) {
                scenarioGroups[groupKey] = node.channelDef;
            }
        });

        // Organisation channels grouped by groupKey
        let channelGrouped = {};
        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelGrouped[groupKey]) {
                channelGrouped[groupKey] = [];
            }
            channelGrouped[groupKey].push(node);
        });

        let groups = [];
        Object.keys(channelGrouped).forEach((groupKey) => {
            if (scenarioGroups[groupKey] && channelGrouped[groupKey].length > 1) {
                groups.push({
                    groupKey,
                    channelDef: scenarioGroups[groupKey],
                    channels: channelGrouped[groupKey],
                });
            }
        });

        return groups.sort((a, b) => {
            let aOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === a.channelDef.realId).order;
            let bOrder = scenario.scenarios2ChannelDefs.edges.find(({node}) => node.channelDef.realId === b.channelDef.realId).order;
            return aOrder - bOrder;
        });
    }

    static getChannelsWitConfidentialMode(scenario, channels) {
        if (!scenario.requiresCaching) {
            return [];
        }

        let scenarioGroupKeys = {};
        scenario.scenarios2ChannelDefs.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            scenarioGroupKeys[groupKey] = true;
        });

        let channelGrouped = {};
        channels.edges.forEach(({node}) => {
            const groupKey = ScenarioApply.getChannelGroupKey(node.channelDef);
            if (!channelGrouped[groupKey]) {
                channelGrouped[groupKey] = [];
            }
            channelGrouped[groupKey].push(node);
        });

        let channelsWithConfidentialMode = [];
        Object.keys(channelGrouped).forEach((groupKey) => {
            if (scenarioGroupKeys[groupKey] && channelGrouped[groupKey].length === 1 && channelGrouped[groupKey][0].confidential) {
                channelsWithConfidentialMode.push(channelGrouped[groupKey][0]);
            }
        });

        return channelsWithConfidentialMode;
    }

    static showFlowSettings(scenario) {
        if (!scenario.scenarioDef.scenarioFlowDefs || !scenario.scenarioDef.scenarioFlowDefs.edges || 0 === scenario.scenarioDef.scenarioFlowDefs.edges.length) {
            return false;
        }

        let flowsWithSettings = scenario.scenarioDef.scenarioFlowDefs.edges.filter(({node}) => !Boolean(node.hideFlowSettingsDuringScenarioApply));

        return flowsWithSettings.length > 0;
    }

    connectChannelStep4Callback(channel) {
        let nextStep = this.state.configurationStep + 1;

        this.onChannelSelect(channel.channelDef, channel);
        this._connectFetchSeq = (this._connectFetchSeq || 0) + 1;
        const fetchSeq = this._connectFetchSeq;
        let applied = false;

        // Relay Classic can invoke this callback more than once. A late repeat
        // must not rewind configurationStep after the user already left this step.
        this.props.relay.forceFetch({}, (readyState) => {
            if (applied || fetchSeq !== this._connectFetchSeq) {
                return;
            }
            if (!readyState || !(readyState.done || readyState.ready)) {
                return;
            }
            applied = true;
            let scenario = this.getScenario();
            let channels = this.props.user.organisation.channels;

            let {alreadyConnectedChannels, channelsToConnect} = this.getChannelsToConnect(scenario, channels);
                alreadyConnectedChannels = ScenarioApply.filterBySkippedGroup(alreadyConnectedChannels, this.state.skippedChannelGroups);
                channelsToConnect = ScenarioApply.filterBySkippedGroup(channelsToConnect, this.state.skippedChannelGroups);
                const channelsToSelect = ScenarioApply.filterBySkippedGroup(ScenarioApply.getChannelsToSelect(scenario, channels), this.state.skippedChannelGroups);
            const resolvedStep = resolvePostConnectWizardStep(nextStep, this.state.configurationStep);

            this.setState({
                configurationStep: resolvedStep,
                alreadyConnectedChannels: alreadyConnectedChannels,
                channelsToConnect: channelsToConnect,
                    channelsToSelect: channelsToSelect,
            });
        });
    }

    renderChannelsToConnect() {
        let channelsToConnect = this.state.channelsToConnect;

        if (channelsToConnect.length === 0 && this.state.alreadyConnectedChannels.length <= 0) {
            return [];
        }

        let channelsToConnectRender = [];

        channelsToConnect.forEach((item, index) => {
            channelsToConnectRender.push({
                title: (<FormattedMessage defaultMessage="Connect {applicationName}" id="channelWizard.connectApplicationName" values={{applicationName: item.channelDef.name}} />),
                description: (<ScenarioChannelConnect
                    channelDef={item.channelDef}
                    connectCallback={(channel) => {
                        return this.connectChannelStep4Callback(channel);
                    }}
                    organisationId={this.props.user.organisation.realId}
                    scenario={this.state.scenario}
                />)
            });
        });

        return channelsToConnectRender;
    }

    renderAlreadyConnectedChannels() {
        return this.state.alreadyConnectedChannels
            .filter((item) => !this.state.skippedChannelGroups[item.groupKey])
            .map((item) => {
            const preset = this.state.channelPresetByGroup[item.groupKey];
            const needsManualEditStep = Boolean(preset && preset.mode === 'edit');
            // Match by channel id only — never by channelDef alone (another existing
            // connection of the same type must not steal the OAuth return).
            const isGrantedChannel = Boolean(
                this.state.grantedChannelId &&
                item.channel &&
                (
                    item.channel.realId === this.state.grantedChannelId ||
                    item.channel.id === this.state.grantedChannelId
                )
            );
            // Stay for post-grant options (4) or unfinished activation (5 + !activated).
            const grantedConnectStep = getGrantedChannelConnectStep(this.state.grantedStep, item.channel);
            const isGrantedChannelPending = Boolean(
                isGrantedChannel && shouldStayOnGrantedChannel(this.state.grantedStep, item.channel)
            );

            if (typeof window !== 'undefined' && this.state.grantedChannelId) {
                console.error('[ScenarioApply] renderAlreadyConnectedChannels channel', {
                    groupKey: item.groupKey,
                    channelRealId: item.channel ? item.channel.realId : null,
                    channelId: item.channel ? item.channel.id : null,
                    channelActivated: item.channel ? item.channel.activated : null,
                    grantedChannelId: this.state.grantedChannelId,
                    isGrantedChannel,
                    isGrantedChannelPending,
                    grantedConnectStep,
                    needsManualEditStep,
                    grantedStep: this.state.grantedStep,
                });
            }

            return {
                finished: !needsManualEditStep && !isGrantedChannelPending,
                title: (needsManualEditStep || isGrantedChannelPending)
                    ? (<FormattedMessage defaultMessage="Configure {app}" id="channelWizard.configureApplicationTitle" values={{app: item.channelDef.name}} />)
                    : (<FormattedMessage defaultMessage="Connected {applicationName}" id="channelWizard.connectedApplicationName" values={{applicationName: item.channelDef.name}} />),
                description: (
                    <ChannelChooser
                        key={isGrantedChannelPending ? ('granted-' + grantedConnectStep) : undefined}
                        channelGroup={{
                            channels: [item.channel],
                            channelDef: item.channelDef,
                            groupKey: item.groupKey,
                        }}
                        connectCallback={(channel) => {
                            return this.connectChannelStep4Callback(channel);
                        }}
                        nextStep={() => { return this.nextStep(); }}
                        onChannelSelect={(channelDef, channel) => { return this.onChannelSelect(channelDef, channel); }}
                        locked={isGrantedChannelPending ? true : Boolean(this.state.lockedChannelGroups[item.groupKey])}
                        preselectedChannelId={isGrantedChannelPending
                            ? this.state.grantedChannelId
                            : (this.state.channelSelect[item.groupKey] ? this.state.channelSelect[item.groupKey].realId : null)}
                        forceConfigure={needsManualEditStep || isGrantedChannelPending}
                        initialStep={isGrantedChannelPending ? grantedConnectStep : undefined}
                        grantedChannelId={isGrantedChannelPending ? this.state.grantedChannelId : undefined}
                        grantedStep={isGrantedChannelPending ? grantedConnectStep : undefined}
                        organisationId={this.props.user.organisation.realId}
                        scenario={this.state.scenario}
                    />
                )
            };
        });
    }

    static getBankScenarioHelpLink(urlFragment) {
        const links = {
            'fio-statements-to-flexibee': {
                bankName: 'Fio banka',
                bankLink: 'https://podpora.flexibee.eu/cs/articles/4489111-fio#h_eb580e4980',
            },
            'rb-eplatby-statements-to-flexibee': {
                bankName: 'Raiffeisenbank',
                bankLink: 'https://podpora.flexibee.eu/cs/articles/3422125-raiffeisenbank#h_c8bc22b0bb',
            },
            'ceska-sporitelna-statements-to-flexibee': {
                bankName: 'Česká spořitelna',
                bankLink: 'https://podpora.flexibee.eu/cs/articles/4473251-ceska-sporitelna#h_7d067c6350',
            },
            'csob-business-connector-statements-to-flexibee': {
                bankName: 'ČSOB',
                bankLink: 'https://podpora.flexibee.eu/cs/articles/4191125-csob#h_3006c80794',
            },
            'kb-statements-to-flexibee': {
                bankName: 'Komerční banka',
                bankLink: 'https://podpora.flexibee.eu/cs/articles/6003745-komercni-banka#h_6bb317466c',
            },
        };

        return links[urlFragment] || null;
    }

    renderBankStatementScenarioGuide(bankHelp) {
        if (!bankHelp) {
            return null;
        }
        const t = (message, values = {}) => this.props.intl.formatMessage(message, values);

        return (
            <div className="longDescription">
                <p><strong>{t(bankGuideMessages.title)}</strong></p>

                <p>{t(bankGuideMessages.intro)}</p>

                <p><strong>{t(bankGuideMessages.setupTitle)}</strong></p>

                <ol>
                    <li><strong>{t(bankGuideMessages.step1Label)}</strong> - {t(bankGuideMessages.step1Body)}</li>
                    <li><strong>{t(bankGuideMessages.step2Label)}</strong> - {t(bankGuideMessages.step2Body)}</li>
                    <li><strong>{t(bankGuideMessages.step3Label)}</strong> - {t(bankGuideMessages.step3Body)}</li>
                    <li><strong>{t(bankGuideMessages.step4Label)}</strong> - {t(bankGuideMessages.step4Body)}</li>
                </ol>

                <p><strong>{t(bankGuideMessages.guidesTitle)}</strong></p>

                <ul>
                    <li>{t(bankGuideMessages.completeGuideLabel)}: <a href="https://podpora.flexibee.eu/cs/articles/15271430-automaticke-online-napojeni-banky" target="_blank" rel="noopener noreferrer">{t(bankGuideMessages.completeGuideLinkText)}</a></li>
                    <li>{t(bankGuideMessages.bankDetailLabel)}: <a href={bankHelp.bankLink} target="_blank" rel="noopener noreferrer">{bankHelp.bankName}</a></li>
                </ul>
            </div>
        );
    }

    renderScenarioInfoStep() {
        const metadata = types[this.typeName];
        const scenario = this.state.scenario;
        const bankScenarioHelp = ScenarioApply.getBankScenarioHelpLink(scenario ? scenario.urlFragment : null);
        const privacy = bankScenarioHelp ? '' : Tool.getFormatted(metadata, 'privacy', this.props.intl.formatMessage, scenario);
        let longDescription = bankScenarioHelp
            ? this.renderBankStatementScenarioGuide(bankScenarioHelp)
            : Tool.getFormatted(metadata, 'longDescription', this.props.intl.formatMessage, scenario);
        let title = <FormattedMessage defaultMessage="Scenario information" id="scenarioApply.infoStep.title" />;

        return {
            title: title,
            description: (
                <div>
                    {privacy ? (
                        <div className="scenario-note big">
                            <span><strong>{Tool.getLabel(types['scenarios'], 'privacy', this.props.intl.formatMessage)}:</strong> {privacy}<br/></span>
                        </div>
                    ) : null}
                    {longDescription ? <div className="longDescription">{longDescription}</div> : null}
                    <a className="btn btn-primary" href="#" onClick={() => { this.nextStep(); return false; }}>
                        <FormattedMessage defaultMessage="Next step" id="channelWizard.skipStep" />
                    </a>
                </div>
            )
        };
    }

    renderError() {
        return (
            <Alert bsStyle="danger">
                <FormattedMessage defaultMessage="Error during scenario apply: {message}" id="channelWizard.apply.error" values={{message: (<strong>{this.state.errors}</strong>)}} />
            </Alert>
        );
    }

    renderFinishStep(finalStep) {
        if (!finalStep) {
            return;
        }

        if (this.state.isSaving || (this.finalSaving && !this.state.flow && !this.state.errors && !this.state.wasSuccess)) {
            return (
                <LoadingLine withMessage>
                    <FormattedMessage defaultMessage="Activating scenario…" id="scanarioActivation.message" />
                </LoadingLine>
            );
        }

        if (this.state.errors) {
            return this.renderError();
        }

        return this.renderResultLinks();
    }

    getDestinationChannelDef() {
        let destinationDefs = this.state.scenario.scenarios2ChannelDefs.edges.filter(({node}) => node.ruleType === 'destination');

        if (destinationDefs.length === 0) {
            return false;
        }

        try {
            return destinationDefs[0].node.channelDef;
        }
        catch (e) {
            return false;
        }
    }

    needInstallExtensionStep() {
        let destinationChannelDef = this.getDestinationChannelDef();

        if (!Boolean(destinationChannelDef)) {
            return false;
        }

        if (this.state.scenario.requiresExtension === false) {
            return false;
        }

        let hasOwnPlugin = Boolean(destinationChannelDef.hasOwnPlugin);

        return !hasOwnPlugin && !isExtensionPresent();
    }

    renderInstallExtenstionStep() {
        let destinationChannelDef = this.getDestinationChannelDef();

        return (
            <div>
                <ChannelIcon channelDef={destinationChannelDef} size={32}/>{" "}<FormattedMessage defaultMessage="To use {channelDefName} with {productName} you must" id="channelWizard.extensionRequired" values={{channelDefName: destinationChannelDef.name, productName: config.PRODUCT_NAME}} />
                {" "}<a className="btn btn-primary" href={getExtensionURL()} onClick={() => {this.nextStep();}} target="_blank">
                    <FormattedMessage defaultMessage="install our Browser Extension" id="channelWizard.extensionRequiredButton" />
                </a>.
                <br /><br />
                <a className="btn btn-primary" href="#" onClick={() => { this.nextStep(); return false;}}>
                    <FormattedMessage defaultMessage="Next step" id="channelWizard.skipStep" />
                </a>
            </div>
        );
    }

    // renderConnectRequirements() {
    // 	let destinationChannelDef = this.getDestinationChannelDef();

    // 	if (!Boolean(destinationChannelDef) || !Boolean(destinationChannelDef.connectRequirement)) {
    // 		return null;
    // 	}

    // 	return (<div>
    // 		<ChannelIcon channelDef={destinationChannelDef} size={32}/>{" "}{Tool.getFormatted(types['channelDefs'], 'connectRequirement', this.props.intl.formatMessage, destinationChannelDef)}
    // 		<br /><br />
    // 		<a className="btn btn-primary" href="#" onClick={() => { this.nextStep(); return false;}}>
    // 			<FormattedMessage defaultMessage="Next step" id="channelWizard.skipStep" />
    // 		</a>
    // 	</div>);
    // }

    getConfigurationSteps() {
        let configurationList = [];
        let showFlowSettings = ScenarioApply.showFlowSettings(this.state.scenario);

        if (this.state.showScenarioInfoStep) {
            let introStep = this.renderScenarioInfoStep();
            if (introStep) {
                configurationList.push(introStep);
            }
        }

        if (this.state.alreadyConnectedChannels.length > 0) {
            configurationList.push(...this.renderAlreadyConnectedChannels());
        }

        configurationList.push(...this.renderChannelsToConnect());

        if (this.state.channelsToSelect.length > 0) {
            configurationList.push(...this.renderChannelChoosers());
        }

        if (this.state.channelsWithConfidentialMode.length > 0) {
            configurationList.push({
                title: (<FormattedMessage defaultMessage="Confidential mode notice" id="channelWizard.confidentialModeNotice" />),
                description: this.renderConfidentialChannels()
            });
        }

        if (showFlowSettings) {
            configurationList.push({
                title: (<FormattedMessage defaultMessage="Flow settings" id="channelWizard.flowSettingsTitle" />),
                description: this.renderFlowSettings(configurationList.length === this.state.configurationStep)
            });
        }

        if (this.needInstallExtensionStep()) {
            configurationList.push({
                title: (<FormattedMessage defaultMessage="Browser extension" id="channelWizard.installBrowserExtension" />),
                description: this.renderInstallExtenstionStep()
            });
        }

        //
        // let connectRequirements = this.renderConnectRequirements();
        // if (connectRequirements) {
        // 	configurationList.push({
        // 		title: (<FormattedMessage defaultMessage="Connect requirements" id="channelWizard.connectRequirements" />),
        // 		description: connectRequirements
        // 	});
        // }

        configurationList.push({
            title: (<FormattedMessage defaultMessage="Activation finished" id="channelWizard.activationFinished" />),
            description: this.renderFinishStep(configurationList.length === this.state.configurationStep)
        });

        return configurationList;
    }

    render() {
        let {scenario} = this.state;
        let type = types[this.typeName];
        let title = this.getTitle(scenario);

        if (this.state.loading) {
            return null;
        }

        this.configurationSteps = this.getConfigurationSteps();

        return (
            <AppPanel breadcrumbParams={[
                {type, data: scenario},
                {type: types['scenarios'], data: scenario},
                {type: types['organisations'], data: this.props.user.organisation}
            ]} className="scenarioWizzardNew" title={title}>

                <DetailBox className="scenarioWizard" data={scenario.scenarioDef} metadata={type} urlMapping={urlMapping}>
                    {getUISettings().style === 'flexibee' && (
                        <div className="flexibeeHeader">
                            <img className="flexibeeHeaderAbra" src="/cdn/logos/logo-abra-flexi.svg" alt="ABRA Flexi logo"  />
                            <span className="flexibeeHeaderPlus">+</span>
                            <img className="flexibeeHeaderDativery" src="/cdn/dativery.svg" alt="Dativery logo" />
                        </div>
                    )}

                    <div className="scenario-header">
                        <div className="left">
                            <ScenarioIcon scenario={scenario} />
                        </div>
                        <div className="main" style={{padding: '0'}}>
                            <span className="flex"></span>
                            <h2>{this.state.showScenarioInfoStep && scenario.title ? Tool.getFormatted(type, 'title', this.props.intl.formatMessage, scenario) : <FormattedMessage defaultMessage="Scenario activation" id="channelWizard.activationHeader" />}</h2>
                            <span className="flex"></span>
                        </div>
                    </div>
                </DetailBox>

                <StepperList activeStep={this.state.configurationStep} list={this.configurationSteps} />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ScenarioApply), (props) => {
    return (props.user.scenarios && props.user.scenarios.edges.length) > 0 || props.user.scenario;
}), {
    initialVariables: {
        scenarioUrlFragment: '',
        scenarioUrlFragmentSelected: false,
        scenarioId: '',
        scenarioIdSelected: false,
        organisationId: '',
        organisationSelected: false,
        flowId: '',
        flowSelected: false,
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        variables['organisationSelected'] = variables['organisationId'] ? true : false;
        variables['flowSelected'] = variables['flowId'] ? true : false;
        variables['scenarioUrlFragmentSelected'] = variables['scenarioUrlFragment'] ? true : false;
        variables['scenarioIdSelected'] = variables['scenarioId'] ? true : false;

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                firstname,
                surname,
                email,
                ${AppliedFlowDescription.getFragment('user')}
                organisation(id: $organisationId) @include(if: $organisationSelected) {
                    id,
                    realId,
                    name,
                    payPlan,
                    ${ScenarioBoxAsWizard.getFragment('organisation')},
                    channels(first: 1000, deleted: false) {
                        edges {
                            node {
                                name,
                                type,
                                confidential,
                                cachingStatus,
                                activated,
                                metadata,
                                lockedAdding,
                                channelDef {
                                    id,
                                    realId,
                                    extendedFrom_id,
                                    extendedFrom {
                                        id,
                                        realId,
                                        icon
                                    },
                                    icon,
                                    addType,
                                    name,
                                    state,
                                    hasOwnPlugin,
                                    metadata,
                                    canBeAddedOnce
                                },
                                id,
                                realId,
                                organisation_id,
                            }
                        },
                    },
                    flow(id: $flowId) @include(if: $flowSelected) {
                        id,
                        realId,
                        organisation_id,
                        configOptions,
                        flowDef {
                            id,
                            realId,
                            appliedDescription,
                        }
                        ${AppliedFlowDescription.getFragment('flow')}
                    },
                    usedScenariosByURL(urlFragment: $scenarioUrlFragment, first: 10) @include(if: $scenarioUrlFragmentSelected) {
                        edges {
                            node {
                                id,
                                realId,
                                scenario_id,
                                scenarioDef_id,
                            }
                        }
                    },
                },
                organisations(first: 10) {
                    edges {
                        node {
                            id,
                        }
                    }
                },
                scenarios(first: 1, urlFragment: $scenarioUrlFragment) @include(if: $scenarioUrlFragmentSelected) {
                    edges {
                        node {
                            id,
                            realId,
                            name,
                            title,
                            description,
                            privacy,
                            longDescription,
                            urlFragment,
                            requiresCaching,
                            requiresExtension,
                            scenarioDef {
                                scenarioFlowDefs(first: 100) {
                                    edges {
                                        node {
                                            realId
                                        }
                                    }

                                }
                            }
                            scenarios2ChannelDefs(first: 100) {
                                edges {
                                    node {
                                        id,
                                        realId,
                                        key,
                                        ruleType,
                                        order,
                                        channelDef {
                                            id,
                                            realId,
                                            extendedFrom_id,
                                            extendedFrom {
                                                id,
                                                realId,
                                                icon
                                            },
                                            name,
                                            icon,
                                            metadata,
                                            hasOwnPlugin,
                                            connectRequirement,
                                            requiresAgent,
                                            canBeAddedOnce
                                        }
                                    }
                                }
                            }
                            ${ScenarioBoxAsWizard.getFragment('scenario')},
                            ${ScenarioIcon.getFragment('scenario')}
                        }
                    }
                },
                scenario(id: $scenarioId) @include(if: $scenarioIdSelected) {
                    id,
                    realId,
                    name,
                    title,
                    description,
                    privacy,
                    longDescription,
                    urlFragment,
                    requiresCaching,
                    requiresExtension,
                    scenarioDef {
                        scenarioFlowDefs(first: 100) {
                            edges {
                                node {
                                    realId,
                                    hideFlowSettingsDuringScenarioApply
                                }
                            }

                        }
                    },
                    scenarios2ChannelDefs(first: 100) {
                        edges {
                            node {
                                id,
                                realId,
                                key,
                                ruleType,
                                order,
                                channelDef {
                                    id,
                                    realId,
                                    extendedFrom_id,
                                    extendedFrom {
                                        id,
                                        realId,
                                        icon
                                    },
                                    name,
                                    icon,
                                    metadata,
                                    hasOwnPlugin,
                                    connectRequirement,
                                    requiresAgent,
                                    canBeAddedOnce
                                }
                            }
                        }
                    }
                    ${ScenarioBoxAsWizard.getFragment('scenario')},
                    ${ScenarioIcon.getFragment('scenario')}
                },
            }
        `,
    },
});
